'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Link, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HttpClient from '@/api/HttpClient';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import { useStartLocationVisit } from '@/hooks/api/useLocationVisits';
import { useNotification } from '@/contexts/NotificationContext';

const apiClient = new HttpClient();

interface LocationVisit {
  id: number;
  date: string;
  locationName: string;
  status: string;
  goaStatus: string;
  trkStatus: string;
  ctmStatus: string;
  atmStatus: string;
}

interface LocationVisitsTableProps {
  locationId: string;
  isAdmin?: boolean;
}

const LocationVisitsTable: React.FC<LocationVisitsTableProps> = ({ locationId, isAdmin = false }) => {
  const [data, setData] = useState<LocationVisit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<any>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [createVisitModalVisible, setCreateVisitModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const startLocationVisitMutation = useStartLocationVisit();
  const { showSuccess, showError } = useNotification();

  const formatDate = (dateValue: string | Date) => {
    if (!dateValue) return '';
    // If it's already a string in YYYY-MM-DD format, use it directly
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // If it's a Date object or other string format, convert it
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    // Use local date to avoid timezone shifts
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const columns = useMemo<MRT_ColumnDef<LocationVisit>[]>(
    () => [
      {
        accessorKey: 'date',
        header: 'Visit Date',
        Cell: ({ row }) => {
          const formattedDate = formatDate(row.original.date);
          return (
            <Link
              component="button"
              variant="body2"
              onClick={() =>
                navigate(`/app/location-service/view/${locationId}/${formattedDate}`)
              }
              underline="hover"
            >
              {formattedDate}
            </Link>
          );
        },
      },
      { accessorKey: 'locationName', header: 'Location' },
      { accessorKey: 'status', header: 'Status' },
      { accessorKey: 'goaStatus', header: 'GOA Status' },
      { accessorKey: 'trkStatus', header: 'TRK Status' },
      { accessorKey: 'ctmStatus', header: 'CTM Status' },
      { accessorKey: 'atmStatus', header: 'ATM Status' },
      ...(isAdmin ? [{
        id: 'actions',
        header: 'Actions',
        Cell: ({ row }: { row: any }) => (
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedVisitId(row.original.id);
              setDeleteModalVisible(true);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      }] : []),
    ],
    [locationId, isAdmin, navigate]
  );

  const fetchData = async () => {
    if (!locationId) return;
    setIsLoading(true);

    try {
      const requestBody = {
        locationId: locationId,
        startDate: '2000-01-01',
        endDate: '2099-12-31',
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search: globalFilter ?? '',
        orderBy: sorting.length ? sorting[0].id : 'date',
        orderDirection: sorting.length ? (sorting[0].desc ? 'DESC' : 'ASC') : 'DESC',
      };
      console.log("requestBody", requestBody);
      const response = await apiClient.post('api/location-visit/query', requestBody);
      console.log("response.data", response.data?.data);
      setData(response.data?.data || []);
      setRowCount(response.data?.total || 0);
    } catch (error) {
      console.error('Error fetching location visits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [locationId, pagination.pageIndex, pagination.pageSize, sorting, globalFilter]);

  const handleCreateVisit = async (values: any) => {
    const dateStr = values.date;
    if (!dateStr) {
      showError('Date is required');
      return;
    }

    // DateField returns date in YYYY-MM-DD format, so we can use it directly
    startLocationVisitMutation.mutate({
      locationId: parseInt(locationId),
      userIds: [], // Empty array - just create the visit without user associations
      date: dateStr
    }, {
      onSuccess: () => {
        showSuccess('Location visit created successfully');
        setCreateVisitModalVisible(false);
        // Refetch the data
        fetchData();
      },
      onError: (error: any) => {
        showError(error.message || 'Failed to create location visit');
      },
    });
  };

  const handleDeleteVisit = async () => {
    if (!selectedVisitId) return;
    if (confirmText.toLowerCase() !== 'confirm') {
      showError('You must type "confirm" to delete this site visit');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await apiClient.post('api/location/delete-site-visit', {
        locationVisitId: selectedVisitId,
      });

      if (response.data?.success) {
        showSuccess('Site visit and all associated data deleted successfully');
        setDeleteModalVisible(false);
        setSelectedVisitId(null);
        setConfirmText('');
        fetchData();
      } else {
        showError(response.data?.message || 'Failed to delete site visit');
      }
    } catch (error: any) {
      showError(error.message || 'Failed to delete site visit');
    } finally {
      setIsDeleting(false);
    }
  };

  const createVisitFields = [
    {
      groupName: "Create Location Visit",
      fields: [
        {
          label: "Visit Date",
          name: "date",
          type: GENERIC_FORM_FIELD_TYPE.DATE,
          required: true,
          columns: 12,
        },
      ],
    },
  ];

  const renderVisitToolbar = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        p: '0.5rem',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="h6">Location Visits</Typography>
      {isAdmin && (
        <IconButton
          color="primary"
          onClick={() => {
            setCreateVisitModalVisible(true);
          }}
        >
          <AddIcon />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      <GenericFormModal
        visible={createVisitModalVisible}
        onDismiss={() => setCreateVisitModalVisible(false)}
        onSubmit={handleCreateVisit}
        initialValues={{}}
        fieldGroups={createVisitFields}
        title="Create Location Visit"
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalVisible}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalVisible(false);
            setSelectedVisitId(null);
            setConfirmText('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Site Visit</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: 'error.main', fontWeight: 'bold' }}>
            Warning: This action cannot be undone!
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Deleting this site visit will permanently remove:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>The site visit record</li>
            <li>All GOA events associated with this visit</li>
            <li>All TRK events associated with this visit</li>
            <li>All ATM events associated with this visit</li>
            <li>All CTM events associated with this visit</li>
            <li>All theft reports associated with this visit</li>
            <li>All reports associated with this visit</li>
            <li>All rejects associated with this visit</li>
            <li>All user associations with this visit</li>
          </Box>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Type <strong>"confirm"</strong> to proceed:
          </Typography>
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type 'confirm' to delete"
            disabled={isDeleting}
            error={confirmText.length > 0 && confirmText.toLowerCase() !== 'confirm'}
            helperText={
              confirmText.length > 0 && confirmText.toLowerCase() !== 'confirm'
                ? 'You must type "confirm" exactly'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteModalVisible(false);
              setSelectedVisitId(null);
              setConfirmText('');
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteVisit}
            color="error"
            variant="contained"
            disabled={isDeleting || confirmText.toLowerCase() !== 'confirm'}
          >
            {isDeleting ? 'Deleting...' : 'Delete Site Visit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2 }}>
        <MaterialReactTable
          columns={columns}
          data={data}
          enableGlobalFilter
          manualPagination
          manualSorting
          onGlobalFilterChange={setGlobalFilter}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={rowCount}
          renderTopToolbarCustomActions={renderVisitToolbar}
          state={{
            isLoading,
            pagination,
            sorting,
            globalFilter,
          }}
          enableBottomToolbar
          enableTopToolbar
          muiTableBodyRowProps={{ hover: true }}
          defaultColumn={{
            minSize: 80,
            maxSize: 400,
            size: 180,
          }}
          initialState={{
            density: 'compact',
            pagination: { pageIndex: 0, pageSize: 10 },
          }}
        />
      </Box>
    </>
  );
};

export default LocationVisitsTable;
